// src/pages/MessagesPage.js
import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { Row, Col, ListGroup, Form, Button, Badge, Modal, Spinner } from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";
import { MessagesContext } from "../MessagesContext";
import AutoGrowTextarea from "../components/AutoGrowTextarea";

// New messages arrive in real time via SSE (see MessagesContext). This slower
// interval is just a safety-net refresh in case the stream is temporarily down.
const LIST_REFRESH_MS = 30000;

function fmtTime(iso) {
  if (!iso) return "";
  // The backend sends naive UTC timestamps with no timezone suffix. Browsers
  // parse such strings as *local* time, so without this the UTC clock value is
  // shown verbatim. Append "Z" (unless already zoned) so it's treated as UTC,
  // then render in Eastern time for this Michigan-based organization.
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const utc = hasZone ? iso : `${iso}Z`;
  try {
    return new Date(utc).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}

export default function MessagesPage() {
  const { user } = useContext(AuthContext);
  const { refreshUnread, lastEvent } = useContext(MessagesContext);

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const bottomRef = useRef(null);
  const activeIdRef = useRef(activeId);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const loadConversations = useCallback(() => {
    api
      .get("/messages/conversations")
      .then((r) => setConversations(r.data?.items || []))
      .catch(() => {});
  }, []);

  const loadThread = useCallback(
    (cid, { markRead = true } = {}) => {
      if (!cid) return;
      api
        .get(`/messages/conversations/${cid}/messages`)
        .then((r) => {
          setMessages(r.data?.items || []);
          if (markRead) {
            api
              .post(`/messages/conversations/${cid}/read`)
              .then(() => {
                refreshUnread();
                setConversations((prev) =>
                  prev.map((c) => (c.id === cid ? { ...c, unread: 0 } : c))
                );
              })
              .catch(() => {});
          }
        })
        .catch(() => {});
    },
    [refreshUnread]
  );

  // Initial + safety-net periodic conversation list refresh.
  useEffect(() => {
    loadConversations();
    const id = setInterval(loadConversations, LIST_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadConversations]);

  // Open a conversation.
  const openConversation = useCallback(
    (cid) => {
      setActiveId(cid);
      setLoadingThread(true);
      api
        .get(`/messages/conversations/${cid}/messages`)
        .then((r) => setMessages(r.data?.items || []))
        .catch(() => {})
        .finally(() => setLoadingThread(false));
      // Mark read separately so the badge clears immediately.
      api
        .post(`/messages/conversations/${cid}/read`)
        .then(() => {
          refreshUnread();
          setConversations((prev) =>
            prev.map((c) => (c.id === cid ? { ...c, unread: 0 } : c))
          );
        })
        .catch(() => {});
    },
    [refreshUnread]
  );

  // React to real-time events pushed over SSE (via MessagesContext).
  useEffect(() => {
    if (!lastEvent || lastEvent.type !== "message") return;
    loadConversations(); // reorder list + refresh unread badges
    if (lastEvent.conversation_id === activeIdRef.current) {
      loadThread(activeIdRef.current, { markRead: true }); // append + mark read
    }
  }, [lastEvent, loadConversations, loadThread]);

  // Auto-scroll to newest.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    api
      .post(`/messages/conversations/${activeId}/messages`, { body })
      .then((r) => {
        setMessages((prev) => [...prev, r.data]);
        setDraft("");
        loadConversations();
      })
      .catch(() => {})
      .finally(() => setSending(false));
  };

  const activeConv = conversations.find((c) => c.id === activeId);

  return (
    <Row className="g-3" style={{ minHeight: "70vh" }}>
      {/* Left: conversation list */}
      <Col md={4} lg={3}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Messages</h5>
          <Button size="sm" variant="primary" onClick={() => setShowNew(true)}>
            <i className="bi bi-pencil-square me-1" /> New
          </Button>
        </div>
        <ListGroup>
          {conversations.length === 0 && (
            <ListGroup.Item className="text-muted">No conversations yet.</ListGroup.Item>
          )}
          {conversations.map((c) => (
            <ListGroup.Item
              key={c.id}
              action
              active={c.id === activeId}
              onClick={() => openConversation(c.id)}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="fw-semibold text-truncate" style={{ maxWidth: "11rem" }}>
                  {c.is_group && <i className="bi bi-people-fill me-1" />}
                  {c.display_name}
                </div>
                {c.unread > 0 && (
                  <Badge bg="danger" pill>
                    {c.unread}
                  </Badge>
                )}
              </div>
              {c.last_message && (
                <div
                  className={`small text-truncate ${
                    c.id === activeId ? "" : "text-muted"
                  }`}
                  style={{ maxWidth: "14rem" }}
                >
                  {c.last_message.body}
                </div>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Col>

      {/* Right: thread */}
      <Col md={8} lg={9} className="d-flex flex-column">
        {!activeId ? (
          <div className="text-muted m-auto">Select a conversation, or start a new one.</div>
        ) : (
          <>
            <h5 className="border-bottom pb-2">
              {activeConv?.is_group && <i className="bi bi-people-fill me-1" />}
              {activeConv?.display_name || "Conversation"}
            </h5>
            <div
              className="flex-grow-1 overflow-auto px-1"
              style={{ maxHeight: "55vh", minHeight: "40vh" }}
            >
              {loadingThread ? (
                <div className="text-center mt-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-muted text-center mt-4">No messages yet — say hello.</div>
              ) : (
                messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div
                      key={m.id}
                      className={`d-flex mb-2 ${mine ? "justify-content-end" : "justify-content-start"}`}
                    >
                      <div
                        className={`p-2 rounded-3 ${mine ? "bg-primary text-white" : "bg-light"}`}
                        style={{ maxWidth: "70%" }}
                      >
                        {!mine && activeConv?.is_group && (
                          <div className="small fw-semibold">{m.sender_name}</div>
                        )}
                        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {m.body}
                        </div>
                        <div
                          className={`small mt-1 ${mine ? "text-white-50" : "text-muted"}`}
                          style={{ fontSize: "0.7rem" }}
                        >
                          {fmtTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>
            <Form onSubmit={send} className="mt-2 d-flex gap-2">
              <AutoGrowTextarea
                rows={1}
                placeholder="Type a message…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(e);
                  }
                }}
              />
              <Button type="submit" disabled={sending || !draft.trim()}>
                Send
              </Button>
            </Form>
          </>
        )}
      </Col>

      <NewConversationModal
        show={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(conv) => {
          setShowNew(false);
          loadConversations();
          openConversation(conv.id);
        }}
      />
    </Row>
  );
}

function NewConversationModal({ show, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setSelected([]);
    setTitle("");
    setError("");
    api
      .get("/messages/users")
      .then((r) => setUsers(r.data?.items || []))
      .catch(() => setUsers([]));
  }, [show]);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const create = () => {
    if (selected.length === 0 || busy) return;
    setBusy(true);
    setError("");
    const isGroup = selected.length > 1;
    api
      .post("/messages/conversations", {
        participant_ids: selected,
        is_group: isGroup,
        title: isGroup ? title.trim() : undefined,
      })
      .then((r) => onCreated(r.data))
      .catch((e) => setError(e?.response?.data?.error || "Could not start conversation."))
      .finally(() => setBusy(false));
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>New message</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <Form.Label>Recipients</Form.Label>
        <div className="border rounded p-2 mb-3" style={{ maxHeight: "16rem", overflowY: "auto" }}>
          {users.length === 0 && <div className="text-muted small">No other users.</div>}
          {users.map((u) => (
            <Form.Check
              key={u.id}
              type="checkbox"
              id={`user-${u.id}`}
              label={`${u.full_name} (${u.username})`}
              checked={selected.includes(u.id)}
              onChange={() => toggle(u.id)}
            />
          ))}
        </div>
        {selected.length > 1 && (
          <Form.Group>
            <Form.Label>Group name (optional)</Form.Label>
            <Form.Control
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Transportation team"
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={create} disabled={selected.length === 0 || busy}>
          {busy ? "Starting…" : "Start"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
